/**
 * "Is this enquiry's person already a contact at the company?" — the duplicate
 * check behind the Enquiries "Create a contact" action.
 *
 * Owner's decision 2026-10-02: duplicates are caught by checking the company's
 * contacts by EMAIL, with no column linking an enquiry to a contact. Contact
 * email is not unique server-side, so this check is the only thing between a
 * second press and a second identical contact.
 *
 * ⚠️ It is a CONVENIENCE, not a guarantee: a contact somebody saves with a
 * different address, or one created between the check and the save, is not
 * seen. Nothing here may be worded as "this person has no contact".
 *
 * ⚠️ `search` matches name, email and title as a substring, so the result is
 * narrowed here to an exact, case-folded match on the primary OR secondary
 * address — a substring hit on somebody else's address is not this person.
 */

import { api } from '@/lib/api';
import type { Contact } from '@/lib/types';

function normalise(email: string): string {
  return email.trim().toLowerCase();
}

/** True when the address is one this check can ask about. */
export function isCheckableEmail(email: string | null | undefined): email is string {
  return typeof email === 'string' && email.trim().includes('@');
}

/**
 * The company's contact holding this address, or `null` when none does.
 * Throws on a failed read — the caller decides what "could not check" means.
 */
export async function findCompanyContactByEmail(
  companyId: number,
  email: string,
  signal?: AbortSignal,
): Promise<Contact | null> {
  const wanted = normalise(email);
  const result = await api.listContacts({ companyId, search: email.trim(), perPage: 20 }, signal);
  return (
    result.data.find(
      (contact) =>
        (contact.email !== null && normalise(contact.email) === wanted) ||
        (contact.secondaryEmail !== null && normalise(contact.secondaryEmail) === wanted),
    ) ?? null
  );
}
